import { KYC_PROVIDER, KYC_SUB_PROVIDER, PRODUCT } from '@constants';
import {
  camelCaseToSpaceSeparated,
  doesExist,
  formatNumberWithCommas,
  getCountryFromCode,
  getKycStatusColor,
  getKycV2StatusColor,
  getUserTierStatusColor,
  kebabCaseToSpaceSeperate,
  toSentenceCase
} from '@utils';
import { formatDate, formatDobDate } from '@utils/dateUtils';


export const toUserDetailsData = (user) => {
  if (!user) return null;

  return {
    ...user,
    countryCode: user.code,
    createdAt: formatDate(user.createdAt),
    kycStatusColor: getKycStatusColor(user.kycStatus),
    kycData: toKycDetailsData(user.kycData),
    phoneNumber: user.number,
    referralCode: user.referralCode,
    freeTransfersLeft: user.numberOfFreeTransfersLeft,
    lifetimeFreeTransfers: user.lifetimeFreeTransfersEnabled
      ? 'enabled'
      : 'not enabled',
    activeDevice: user.activeDevice
      ? {
          ...user.activeDevice,
          lastLoginAt: formatDate(user.activeDevice?.lastLoginAt)
        }
      : null
  };
};

export const getUserDetailsList = (userData) => [
  {
    name: 'User ID:',
    value: userData?.userId,
    isCopyable: true
  },
  {
    name: 'Username:',
    value: userData?.name,
    isCopyable: true,
    isEditable: true
  },
  {
    name: 'Kyc Name:',
    value: userData?.kycName,
    isCopyable: true,
    isEditable: true
  },

  {
    name: 'Mobile number:',
    value: userData?.phoneNumber,
    isCopyable: true,
    isEditable: true
  },
  {
    name: 'Email:',
    value: userData?.email,
    isCopyable: true,
    isEditable: true
  },
  {
    name: 'Date of birth:',
    value: formatDobDate(userData?.userProfile?.dateOfBirth) || '-',
    isCopyable: true,
    isEditable: true
  },
  {
    name: 'Country:',
    value: getCountryFromCode(userData?.code)
  },
  {
    name: 'Referrer Code:',
    value: userData?.referrerCode || 'User joined independently'
  },
  {
    name: 'Created on',
    value: userData?.createdAt
  },

  {
    name: 'App Platform:',
    value: userData?.activeDevice?.appPlatform
  },
  {
    name: 'Free transfers left:',
    value: doesExist(userData?.freeTransfersLeft)
      ? userData?.freeTransfersLeft
      : '-'
  },
  {
    name: 'Lifetime free transfers:',
    value: doesExist(userData?.lifetimeFreeTransfers)
      ? userData?.lifetimeFreeTransfers
      : '-'
  },
  {
    name: 'User Type:',
    value: doesExist(userData?.userType) ? userData?.userType : '-'
  },
  {
    name: 'Vance Cash:',
    value: doesExist(userData?.vanceCash) ? userData?.vanceCash : '-'
  },
  {
    name: 'Aspora Cash:',
    value: doesExist(userData?.vanceCashV2)
      ? userData?.vanceCashV2 + ' ' + userData?.currencyV2
      : '-'
  },
  {
    name: 'Aspora Coins:',
    value: doesExist(userData?.vanceCoinsV2)
      ? userData?.vanceCoinsV2 + ' ' + userData?.currencyV2
      : '-'
  },
  {
    name: 'User Profile: ',
    value: "Open",
  },
  {
    name: 'Features Whitelisted:',
    value: userData?.featuresWhitelisted,
    isArray: true
  }
];

const toKycDetailsData = (kycData) => {
  if (!kycData) return null;

  return {
    ...kycData,
    agentRefId: kycData['agentReference'],
    dob: formatDobDate(kycData['contactDob']),
    email: kycData['contactEmail'],
    firstName: kycData['contactFname'],
    lastName: kycData['contactLname'],
    gender: kycData['contactGender'],
    nationality: kycData['contactNationality'],
    phoneNumber: kycData['contactPhone'],
    salutation: kycData['contactTitle'],
    createdDate: formatDate(kycData['dateCreated']),
    fileLinkA: kycData['fileA'],
    fileLinkB: kycData['fileB'],
    kycVerifiedAt: formatDate(kycData.kycVerifiedAt),

    address: {
      city: kycData.addressCity,
      countryCode: kycData.addressCountry,
      postCode: kycData.addressPostcode,
      premise: kycData.addressPremise,
      region: kycData.addressRegion,
      street: kycData.addressStreet
    }
  };
};

export const toUserKycAddress = (address) => ({
  ...address
});

export const getUserAddressFieldsConfig = (userAddress = {}) => [
  {
    name: 'premise',
    label: 'Premise',
    fieldType: 'input',
    inputType: 'string',
    isRequired: true,
    value: userAddress?.address1 || '',
    isEditable: true,
  },
  {
    name: 'street',
    label: 'Street',
    fieldType: 'input',
    inputType: 'string',
    isRequired: true,
    value: userAddress?.address2 || '',
    isEditable: true,
  },
  {
    name: 'city',
    label: 'City',
    fieldType: 'input',
    inputType: 'string',
    isRequired: true,
    value: userAddress?.city || '',
    isEditable: true,
  },
  {
    name: 'postCode',
    label: 'Post Code',
    fieldType: 'input',
    inputType: 'string',
    isRequired: true,
    value: userAddress?.postCode || '',
    isEditable: true,
  },
];

export const getKycDetailsList = (kycData) => [
  {
    name: 'Name',
    value: `${kycData?.firstName} ${kycData?.lastName}`
  },
  {
    name: 'Title',
    value: kycData?.salutation
  },
  {
    name: 'Gender',
    value: kycData?.gender
  },
  {
    name: 'Date of birth',
    value: kycData?.dob
  },
  {
    name: 'Nationality',
    value: kycData?.nationality
  },
  {
    name: 'Address line 1',
    value: kycData?.address?.premise
  },
  {
    name: 'Address line 2',
    value: kycData?.address?.street
  },
  {
    name: 'City',
    value: kycData?.address?.city
  },
  {
    name: 'Post code',
    value: kycData?.address?.postCode
  },
  {
    name: 'RFX Client Id',
    value: kycData?.clientId,
    isCopyable: true
  },
  {
    name: 'KYC Rejection Reason',
    value: kycData?.kycRejectionReason
  },
  {
    name: 'KYC Verified at',
    value: kycData?.kycVerifiedAt
  }
];

export const getActiveDeviceDetailsList = (device) =>
  device
    ? [
        {
          name: 'App version code',
          value: device.appVersionCode
        },
        {
          name: 'App version name',
          value: device.appVersionName
        },
        {
          name: 'Device model',
          value: device.deviceModel
        },
        {
          name: 'Device name',
          value: device.deviceName
        },
        {
          name: 'Device brand',
          value: device.deviceBrand
        },
        {
          name: 'Device id',
          value: device.deviceId
        },
        {
          name: 'Device manufacturer',
          value: device.deviceManufacturer
        },
        {
          name: 'Device OS version',
          value: device.deviceOsVersion
        },
        {
          name: 'Last login at',
          value: device.lastLoginAt
        }
      ]
    : null;

export const getVanceKycDetailsList = (kycData) => [
  {
    name: 'Title',
    value: kycData.title || '-'
  },
  {
    name: 'Provider',
    value: KYC_PROVIDER.VANCE
  },
  {
    name: 'Product',
    value: PRODUCT.REMITTANCE
  },
  {
    name: 'Last Updated At',
    value: formatDate(kycData?.lastUpdatedAt) || '-'
  },
  {
    name: 'Created At',
    value: formatDate(kycData?.createdAt) || '-'
  },
  {
    name: 'KYC Rejection Reasons',
    value: JSON.stringify(kycData?.rejectionReasons)
  },
  {
    name: 'KYC Rejection Count',
    value: kycData?.rejectionCount || '-'
  }
];

export const getVanceKycDetails = (vanceKyc) => {
  if(!vanceKyc || Object.keys(vanceKyc).length === 0) {
    return null;
  }
  return {
    title: 'Vance User KYC',
    color: getKycV2StatusColor(vanceKyc.kycStatus) || 'white',
    value: vanceKyc?.kycStatus || '-',
    provider: KYC_PROVIDER.VANCE,
    detailsLink: null,
    showLink: false,
    showTooltip: true,
    divider: true
  };
};

export const getKycV2DetailsTable = (kycs, userId) => {
  const groupedKycObj = {};

  kycs?.forEach((kyc) => {
    const { product, provider, subProvider = KYC_SUB_PROVIDER.FULL_KYC_TIER } = kyc;

    // Note :: Only including Remittance as Product
    if(product.toUpperCase() !== PRODUCT.REMITTANCE) return;

    const updatedKyc = {
      ...kyc,
      title: toSentenceCase(kebabCaseToSpaceSeperate(provider)),
      statusColor: getKycV2StatusColor(kyc?.kycStatus) || 'white',
      detailsLink: `/user/${userId}/kyc/product/${product}/provider/${provider}/sub-provider/${subProvider}`,
      showLink: !!kyc?.kycStatus,
    };

    // Level 1: Group by Product
    if (!groupedKycObj[product]) {
      groupedKycObj[product] = {};
    }

    // Level 2: Group by Provider
    if (!groupedKycObj[product][provider]) {
      groupedKycObj[product][provider] = [];
    }

    groupedKycObj[product][provider].push(updatedKyc);
  });

  return groupedKycObj;
};

export const SUPPORTED_USER_DETAILS_UPDATE_FIELDS = Object.freeze({
  EMAIL: 'email',
  KYC_NAME: 'kycName',
  NAME: 'name',
  PHONE_NUMBER: 'phoneNumber',
  DOB: 'dob'
});

export const TAB_STATES = {
  USER: 'USER',
  RECIPIENT: 'RECIPIENT',
  SOURCE_ACCOUNT: 'SOURCE_ACCOUNT',
};

export const getAbstractedUserTiers = (tierData) => {
  if (!tierData || !tierData.tiersEnabled || !tierData.tiers) return null;

  const tiers = Object.entries(tierData.tiers).map(([key, tier]) => {
    const {
      tierId,
      tierStatus,
      activationStatus,
      previousStatus,
      previousActivationStatus,
      createdAt,
      updatedAt,
      tierLimits,
      tierConsumption,
      allowedTransitions,
    } = tier;

    const totalLimit = tierLimits?.totalTransferAmount ?? null;
    const consumed = tierConsumption?.totalTransferAmount ?? 0;

    const progress = totalLimit ? Math.min((consumed / totalLimit) * 100, 100) : null;

    return {
      tierKey: key,
      tierId: kebabCaseToSpaceSeperate(tierId),
      tierStatus,
      tierStatusColor: getUserTierStatusColor(tierStatus) || 'white',
      activationStatus,
      previousStatus,
      previousActivationStatus,
      createdAt,
      updatedAt,
      allowedTransitions,
      limit: totalLimit,
      consumed,
      progress,
    };
  });

  return {
    tiersEnabled: tierData.tiersEnabled,
    initialTier: tierData.initialTier,
    tiers,
  };
};

export const getAbstractedUserTierTable = (tiersData) => {
  if (!tiersData) return [];

  const {
    initialTier,
    tiersEnabled
  } = tiersData;

  return [
    {
      title: 'User Tiers Overview',
      data: [
        {
          name: 'Initial Tier:',
          value: toSentenceCase(kebabCaseToSpaceSeperate(initialTier)) || 'N/A',
        },
        {
          name: 'Tiers Enabled:',
          value: tiersEnabled ? "TRUE" : "FALSE",
        },
      ]
    }
  ];
}

export const getSelectedTierTable = (tier) => {
  if (!tier) return [];

  const {
    tierKey,
    tierId,
    tierStatus,
    activationStatus,
    previousStatus,
    previousActivationStatus,
    createdAt,
    updatedAt,
    allowedTransitions,
    limit,
    consumed,
    progress,
  } = tier;


  return [
    {
      title: 'Tier Metadata',
      data: [
        {
          name: 'Tier Key:',
          value: camelCaseToSpaceSeparated(tierKey) || 'N/A',
        },
        {
          name: 'Tier ID:',
          value: kebabCaseToSpaceSeperate(tierId) || 'N/A',
          isCopyable: true,
        },
        {
          name: 'Tier Status:',
          value: tierStatus || 'N/A',
        },
        {
          name: 'Activation Status:',
          value: activationStatus || 'N/A',
        },
        {
          name: 'Previous Status:',
          value: previousStatus || 'N/A',
        },
        {
          name: 'Previous Activation Status:',
          value: previousActivationStatus || 'N/A',
        },
        {
          name: 'Total Limit:',
          value: formatNumberWithCommas(limit) || '0',
        },
        {
          name: 'Consumed:',
          value: formatNumberWithCommas(consumed) || '0',
        },
        {
          name: 'Progress %:',
          value: `${progress ? progress + "%" : "N/A"}`,
        },
        {
          name: 'Created At:',
          value: createdAt ? formatDate(createdAt) : 'N/A',
        },
        {
          name: 'Updated At:',
          value: updatedAt ? formatDate(updatedAt) : 'N/A',
        },
        {
          name: 'Next States:',
          value: Array.isArray(allowedTransitions)
            ? allowedTransitions.join(', ')
            : 'N/A',
        },
      ],
    },
  ];
};